/**
 * Header switcher — toggles between classic Dashboard (`/app`) and the
 * Day 9 Ops Center (`/app/ops-center`).
 *
 * Persists last-used route in localStorage so the operator returns to
 * their preferred view on next sign-in. The shell currently renders the
 * route the user navigated to (no auto-redirect on mount), but the
 * persisted value is exposed for the Login page so a future session
 * remembers the choice.
 */

import { Link, useLocation } from "wouter";
import { LayoutDashboard, ListTree } from "lucide-react";

export const APP_HOME_STORAGE_KEY = "sentinel_app_home";

export function persistAppHome(path: "/app" | "/app/ops-center"): void {
  try {
    localStorage.setItem(APP_HOME_STORAGE_KEY, path);
  } catch {
    /* SSR / storage disabled */
  }
}

export function loadAppHome(): "/app" | "/app/ops-center" {
  try {
    const v = localStorage.getItem(APP_HOME_STORAGE_KEY);
    if (v === "/app/ops-center") return v;
    return "/app";
  } catch {
    return "/app";
  }
}

export function HeaderSwitcher() {
  const [location] = useLocation();
  const tabs = [
    {
      path: "/app" as const,
      label: "Dashboard",
      icon: LayoutDashboard,
      testId: "header-tab-dashboard",
    },
    {
      path: "/app/ops-center" as const,
      label: "Ops Center",
      icon: ListTree,
      testId: "header-tab-ops-center",
    },
  ];
  return (
    <nav
      className="inline-flex rounded-md border border-slate-700 overflow-hidden text-xs"
      data-testid="header-switcher"
      aria-label="App view"
    >
      {tabs.map(({ path, label, icon: Icon, testId }) => {
        const active = location === path;
        return (
          <Link
            key={path}
            href={path}
            data-testid={testId}
            onClick={() => persistAppHome(path)}
            className={
              `inline-flex items-center gap-1.5 px-3 py-1.5 transition ` +
              (active
                ? "bg-teal-500/20 text-teal-200"
                : "bg-slate-900/40 text-slate-400 hover:bg-slate-800/60")
            }
          >
            <Icon className="size-3.5" />
            <span>{label}</span>
          </Link>
        );
      })}
    </nav>
  );
}
